import { useLibrary, useSaved } from '@echo/core/react';
import { Button } from '@echo/ui-web';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, useCanGoBack, useNavigate, useRouter } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, Heart } from 'lucide-react';
import { BookHeading } from '@/components/library-items';

/** `?category=` is the category the book was opened from, so its tag names that one. */
interface BookSearch {
  category?: string;
}

export const Route = createFileRoute('/_app/library_/book/$lineageUuid')({
  component: BookInfoPage,
  validateSearch: (search: Record<string, unknown>): BookSearch =>
    typeof search.category === 'string' && search.category ? { category: search.category } : {},
});

/**
 * Library › book info (Figma 3530:77729): what a book is about before reading it. The book,
 * its two-sentence summary, "Read now" (through the coach, like the chooser's choices) and
 * "Favorite", which keeps it in Library › My favorites; the heart in the toolbar does the same.
 */
function BookInfoPage() {
  const { lineageUuid } = Route.useParams();
  const { category } = Route.useSearch();
  const { t } = useLingui();
  const navigate = useNavigate();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const library = useLibrary();
  const saved = useSaved();
  const entry = library.reading.find((candidate) => candidate.lineageUuid === lineageUuid);
  const isSaved = saved.isSaved(lineageUuid);

  const back = () => (canGoBack ? router.history.back() : navigate({ to: '/library' }));
  const read = () =>
    void navigate({
      to: '/exercise/$kind/intro',
      params: { kind: 'reading' },
      state: { exercise: { source: 'workbook', passage: lineageUuid } },
    });
  const toggle = () => entry && saved.toggle(entry);

  return (
    <div className="flex w-full flex-col gap-content-sm">
      <div className="flex min-h-tap-target items-center justify-between gap-content-sm">
        <Button variant="ghost" onClick={back}>
          <ArrowLeft size={24} aria-hidden />
          {t`Back`}
        </Button>
        {entry && saved.available ? (
          <button
            type="button"
            onClick={toggle}
            aria-pressed={isSaved}
            aria-label={isSaved ? t`Remove favorite` : t`Favorite`}
            className="flex size-tap-target cursor-pointer items-center justify-center rounded-full bg-surface text-on-action-secondary shadow-task-row focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-highlight"
          >
            <Heart size={24} aria-hidden fill={isSaved ? 'currentColor' : 'none'} />
          </button>
        ) : null}
      </div>

      {!library.ready ? (
        <div className="flex justify-center py-section-sm">
          <span
            aria-hidden
            className="size-10 animate-spin rounded-full border-4 border-action-primary border-t-transparent"
          />
        </div>
      ) : !entry ? (
        <p className="text-body-md text-on-surface-medium">
          <Trans>There's nothing here yet.</Trans>
        </p>
      ) : (
        <section className="flex flex-col gap-content-lg rounded-xl border border-border-weak bg-surface px-content-sm py-content-md">
          <BookHeading entry={entry} tags={library.tagsFor(entry, category)} />
          {entry.summary ? (
            <p className="text-body-md text-on-surface-medium">{entry.summary}</p>
          ) : null}
          <div className="flex flex-col gap-content-sm">
            <Button onClick={read}>
              {t`Read now`}
              <ArrowRight size={24} aria-hidden />
            </Button>
            {saved.available ? (
              <Button variant="tertiary" onClick={toggle} aria-pressed={isSaved}>
                <Heart size={24} aria-hidden fill={isSaved ? 'currentColor' : 'none'} />
                {isSaved ? t`Remove favorite` : t`Favorite`}
              </Button>
            ) : null}
          </div>
        </section>
      )}
    </div>
  );
}
